import { useCallback, useEffect, useRef, useState } from 'react';
import { apiRequest } from '../services/apiClient';

/**
 * Concurrent multi-URL data-fetching hook.
 * Fires all requests in parallel and resolves them together.
 * Eliminates the repeated Promise.all + isMounted + loading/error pattern.
 *
 * @param {{ [key: string]: string | null }} urlMap - Keys are result names; values are API paths.
 *   Pass null for any URL to skip that entry (useful for conditional fetches).
 * @param {object} [options]
 * @param {Array}  [options.deps=[]] - Dependencies that trigger a full refetch when they change.
 *   Include any values interpolated into the URL strings.
 * @returns {{ data: { [key: string]: any }, isLoading, error, refetch }}
 */
export function useMultiFetch(urlMap, { deps = [] } = {}) {
  const [data, setData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const abortRef = useRef(null);
  const urlMapRef = useRef(urlMap);

  // Keep ref current on every render without triggering refetches.
  useEffect(() => {
    urlMapRef.current = urlMap;
  });

  const fetch = useCallback(async () => {
    const entries = Object.entries(urlMapRef.current).filter(([, url]) => url);

    if (!entries.length) {
      setIsLoading(false);
      return;
    }

    if (abortRef.current) {
      abortRef.current.abort();
    }

    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    setError('');

    try {
      const results = await Promise.all(
        entries.map(([, url]) => apiRequest(url, { signal: controller.signal }))
      );

      if (!controller.signal.aborted) {
        const next = {};
        entries.forEach(([key], index) => { next[key] = results[index]; });
        setData(next);
      }
    } catch (err) {
      if (!controller.signal.aborted) {
        setError(err.message || 'Unable to load data. Check your connection and try again.');
      }
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    fetch();
    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, [fetch]);

  return { data, isLoading, error, refetch: fetch };
}
